/**
 * IRS Publication 1220 flat-file intake.
 *
 * A customer who already files through the FIRE-era pipeline has a 750-character fixed-width
 * file, not a spreadsheet. This reads one and hands back records in the same vocabulary
 * checkRecords and the per-form generators expect, so a Pub 1220 file and an uploaded CSV meet
 * at exactly the same place.
 *
 * The positions come from `pub1220Layouts.ts`, which is a verbatim copy of the layout arrays
 * core-api uses to WRITE these files. Reading with the same arrays that write is what keeps
 * the two halves from drifting.
 *
 * This module is pure: text in, records out. It never decides whether a record is fileable,
 * which is checkRecords' job.
 */
import { classifyName } from "./nameIdentification/classifyName";
import { splitPersonNameWithControl } from "./nameIdentification/splitWithNameControl";
import { readForeignCityBlock, US_STATE_CODES, type ForeignAddressReading } from "./foreignAddress1220";
import type { NameClassification } from "./nameIdentification/types";
import type { FormType } from "../context";
import {
  A_RECORD,
  B_RECORD_COMMON,
  C_RECORD,
  FORM_BLOCK_544_750,
  F_RECORD,
  PUB1220_RECORD_LENGTH,
  Pub1220Field,
  T_RECORD,
} from "./pub1220Layouts";
import {
  amountCodeForSlot,
  amountColumn,
  TYPE_OF_RETURN_TO_FORM,
} from "./pub1220Codes";
import {
  readTailValue,
  TAIL_CARRIED_ONLY,
  TAIL_MAPPING_BY_FORM,
} from "./pub1220Tail";

export interface Pub1220Issuer {
  tin: string;
  name: string;
  formType: FormType | null;
  rawTypeOfReturn: string;
  recordCount: number;
}

/** One foreign address block the parser read, and how sure it is of the reading. */
export interface Pub1220ForeignAddress {
  line: number;
  /** Whose address: the payee (B), the issuer (A), or the 1099-LTC insured (B, 637-687). */
  role: "payee" | "issuer" | "insured";
  /** The payee record's reference (account number or line-N); the issuer's TIN for an A record. */
  ref: string;
  formType: FormType | null;
  reading: ForeignAddressReading;
}

/**
 * The name a payee record was filed under, as the file wrote it: one name line, surname first by
 * Pub 1220's convention, with the name control and the Type of TIN beside it. The browser shows
 * these on Resolve Names so the person sees how each was read before anything is uploaded. The
 * TIN itself is not carried here; only what kind it was declared to be.
 */
export interface Pub1220PayeeName {
  /** Index into `records`. */
  recordIndex: number;
  line: number;
  nameLine: string;
  secondNameLine: string;
  nameControl: string;
  /** The Type of TIN field: "1" an EIN, "2" an SSN/ITIN/ATIN, "" not given. */
  typeOfTin: string;
}

/** How to read a file whose names someone has already reviewed. */
export interface Pub1220ParseOptions {
  /**
   * The person-or-business answer for a name line, when one was given on Resolve Names. It
   * replaces the reading from the Type of TIN and the name rules; a 1098-T student stays a
   * person whatever it says.
   */
  classificationFor?: (nameLine: string) => NameClassification | undefined;
}

export interface Pub1220ParseResult {
  /** Set when exactly one form type is present in the file. */
  formType: FormType | null;
  /** From the T record's PaymentYear. */
  taxYear: number | null;
  /** Every Type of Return code seen, as the canonical form name when known. */
  formTypes: string[];
  /** One per A record. */
  issuers: Pub1220Issuer[];
  /** Canonical records, in file order. */
  records: Record<string, unknown>[];
  /** Structural problems: bad record length, unknown record type, C-record count mismatch. */
  errors: { line: number; message: string }[];
  warnings: { line: number; message: string }[];
  /** Every foreign address read from a free-format city/state/ZIP block, in file order. */
  foreignAddresses: Pub1220ForeignAddress[];
  /** One per record, in the same order: the name line each payee was read from. */
  payeeNames: Pub1220PayeeName[];
}

// ---------------------------------------------------------------------------
// Splitting the file into records
// ---------------------------------------------------------------------------

/** Every record type Pub 1220 defines, so an unknown letter can be reported as such. */
const KNOWN_RECORD_TYPES = new Set(["T", "A", "B", "C", "K", "F"]);

/**
 * The file's records, one per entry, however the writer separated them.
 *
 * Most writers end each 750-character record with a line break. Some write the records back to
 * back with none at all, and some lose a break here and there, leaving two records on one line.
 * A line longer than a record is cut into 750-character records, but only when every piece
 * starts with a Pub 1220 record type; anything else is left whole so the length check reports
 * it. `joined` counts the lines that had to be cut.
 */
export function splitPub1220Lines(text: string): { lines: string[]; joined: number } {
  const out: string[] = [];
  let joined = 0;
  for (const raw of String(text ?? "").split(/\r\n|\r|\n/)) {
    const line = raw.replace(/[\r\n]+$/, "");
    if (line.length <= PUB1220_RECORD_LENGTH + 2) {
      out.push(line);
      continue;
    }
    const pieces: string[] = [];
    for (let at = 0; at < line.length; at += PUB1220_RECORD_LENGTH) {
      pieces.push(line.slice(at, at + PUB1220_RECORD_LENGTH));
    }
    // Trailing padding after the last record is not a record. A last record whose blank
    // trailing positions were stripped is filled back out: those positions are blanks.
    if (pieces.length > 1 && pieces[pieces.length - 1].trim() === "") pieces.pop();
    if (pieces.length > 1) {
      pieces[pieces.length - 1] = pieces[pieces.length - 1].padEnd(PUB1220_RECORD_LENGTH, " ");
    }
    const whole = pieces.every((piece) => KNOWN_RECORD_TYPES.has(piece.charAt(0).toUpperCase()));
    if (pieces.length > 1 && whole) {
      out.push(...pieces);
      joined++;
    } else {
      out.push(line);
    }
  }
  return { lines: out, joined };
}

// ---------------------------------------------------------------------------
// Reading fields off a fixed-width line
// ---------------------------------------------------------------------------

/** Field name -> zero-based offset and length, computed once per layout. */
function offsetsFor(layout: Pub1220Field[]): Map<string, { at: number; len: number }> {
  const map = new Map<string, { at: number; len: number }>();
  let at = 0;
  for (const f of layout) {
    if (f.key) map.set(f.key, { at, len: f.length });
    at += f.length;
  }
  return map;
}

const T_OFFSETS = offsetsFor(T_RECORD);
const A_OFFSETS = offsetsFor(A_RECORD);
const C_OFFSETS = offsetsFor(C_RECORD);
const F_OFFSETS = offsetsFor(F_RECORD);

/** B record offsets per form: the common head plus that form's 544-onward tail. */
const B_OFFSETS_CACHE = new Map<string, Map<string, { at: number; len: number }>>();
function bOffsets(form: string | null): Map<string, { at: number; len: number }> {
  const key = form ?? "";
  const hit = B_OFFSETS_CACHE.get(key);
  if (hit) return hit;
  const tail = (form && FORM_BLOCK_544_750[form]) || [];
  const built = offsetsFor([...B_RECORD_COMMON, ...tail]);
  B_OFFSETS_CACHE.set(key, built);
  return built;
}

function read(
  line: string,
  offsets: Map<string, { at: number; len: number }>,
  key: string
): string {
  const spec = offsets.get(key);
  if (!spec) return "";
  return line.substring(spec.at, spec.at + spec.len).trim();
}

/** Cents as dollars for a message: 123456n -> "1,234.56", -1250n -> "-12.50". */
function centsText(cents: bigint): string {
  const sign = cents < 0n ? "-" : "";
  const abs = cents < 0n ? -cents : cents;
  const whole = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${whole}.${(abs % 100n).toString().padStart(2, "0")}`;
}

/**
 * An amount field in cents. Pub 1220 amounts are zero-filled cents, and only a loss on 1099-B,
 * 1099-OID or 1099-Q may be negative, marked by a "-" in the left-most position ("+" or no
 * sign is positive). Reading only the digits turned a 1099-Q earnings loss into a gain.
 */
function signedCents(raw: string): bigint {
  const value = String(raw || "").trim();
  const digits = value.replace(/\D/g, "");
  if (!digits) return 0n;
  return value.startsWith("-") ? -BigInt(digits) : BigInt(digits);
}

/**
 * "000000123456" is 1234.56 and "-00000012345" is -123.45. An all-zero or blank field is not
 * a value: the form simply has no amount for that code.
 */
export function centsToDecimal(raw: string): string | null {
  const cents = signedCents(raw);
  if (cents === 0n) return null;
  const abs = (cents < 0n ? -cents : cents).toString().padStart(3, "0");
  return `${cents < 0n ? "-" : ""}${abs.slice(0, -2)}.${abs.slice(-2)}`;
}

// ---------------------------------------------------------------------------
// Where each form's payee columns live in the canonical vocabulary
// ---------------------------------------------------------------------------

/**
 * The B record always describes one payee. What that payee is CALLED differs per form, so
 * this table names the canonical columns to write. Every name here exists in that form's
 * interface (see validate/formFields.ts); nothing is invented.
 */
interface PayeeTargets {
  tin: string;
  firstName: string;
  lastName: string;
  businessName: string;
  nameControl: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  accountNumber: string;
  stateTaxWithheld?: string;
}

const RECIPIENT_TARGETS: PayeeTargets = {
  tin: "RECIPIENT_ID_NUMBER",
  firstName: "RECIPIENT_FIRST_NAME",
  lastName: "RECIPIENT_LAST_NAME",
  businessName: "RECIPIENT_BUSINESS_NAME1",
  nameControl: "RECIPIENT_NAME_CONTROL",
  address1: "RECIPIENT_ADDRESS1",
  city: "RECIPIENT_CITY",
  state: "RECIPIENT_STATE",
  zip: "RECIPIENT_ZIP",
  accountNumber: "ACCOUNT_NUMBER",
};

export const PAYEE_TARGETS_BY_FORM: Record<string, PayeeTargets> = {
  "1099-NEC": { ...RECIPIENT_TARGETS, stateTaxWithheld: "BOX5_STATE_TAX_WITHHELD1" },
  "1099-MISC": { ...RECIPIENT_TARGETS, stateTaxWithheld: "BOX16_STATE_TAX_WITHHELD1" },
  "1099-INT": { ...RECIPIENT_TARGETS, stateTaxWithheld: "BOX17_STATE_TAX_WITHHELD" },
  "1099-DIV": { ...RECIPIENT_TARGETS, stateTaxWithheld: "BOX16_STATE_TAX_WITHHELD1" },
  "1099-R": { ...RECIPIENT_TARGETS, stateTaxWithheld: "BOX14_STATE_TAX_WITHHELD1" },
  "1099-C": { ...RECIPIENT_TARGETS },
  "1099-B": { ...RECIPIENT_TARGETS, stateTaxWithheld: "BOX_16_STATE_TAX_WITHHELD1" },
  "1099-Q": { ...RECIPIENT_TARGETS },
  "1099-SA": {
    ...RECIPIENT_TARGETS,
    tin: "BOX_RECIPIENT_ID_NUMBER",
    accountNumber: "BOX_ACCOUNT_NUMBER",
  },
  "5498-SA": {
    ...RECIPIENT_TARGETS,
    tin: "BOX_RECIPIENT_ID_NUMBER",
    accountNumber: "BOX_ACCOUNT_NUMBER",
  },
  "1099-K": {
    tin: "PAYEE_TIN",
    firstName: "PAYEE_NAME",
    lastName: "PAYEE_NAME2",
    businessName: "PAYEE_NAME",
    nameControl: "PAYEE_NAME_CONTROL",
    address1: "PAYEE_ADDRESS1",
    city: "PAYEE_CITY",
    state: "PAYEE_STATE",
    zip: "PAYEE_ZIP",
    accountNumber: "PAYEE_INTERNAL_ACCT_NO",
    stateTaxWithheld: "BOX8A_STATE_INCOME_TAX_WTHLD",
  },
  "1099-LTC": {
    tin: "POLICYHOLDER_TAX_ID",
    firstName: "POLICYHOLDER_NAME",
    lastName: "POLICYHOLDER_NAME2",
    businessName: "POLICYHOLDER_NAME",
    nameControl: "POLICYHOLDER_NAME_CONTROL",
    address1: "POLICYHOLDER_STREET_ADDRESS_1",
    city: "POLICYHOLDER_CITY",
    state: "POLICYHOLDER_STATE",
    zip: "POLICYHOLDER_ZIP_CODE",
    accountNumber: "INSURED_ACCOUNT_NUMBER",
  },
  "1098-E": {
    tin: "BORROWER_SSN",
    firstName: "BORROWER_NAME",
    lastName: "BORROWER_NAME2",
    businessName: "BORROWER_NAME",
    nameControl: "BORROWER_NAME_CONTROL",
    address1: "BORROWER_ADDRESS1",
    city: "BORROWER_CITY",
    state: "BORROWER_STATE",
    zip: "BORROWER_ZIP",
    accountNumber: "BORROWER_ACCOUNT_NUMBER",
  },
  "1098-T": {
    tin: "STUDENT_SSN",
    firstName: "STUDENT_FIRST_NAME",
    lastName: "STUDENT_LAST_NAME",
    businessName: "STUDENT_NAME",
    nameControl: "STUDENT_NAME_CONTROL",
    address1: "STUDENT_ADDRESS1",
    city: "STUDENT_CITY",
    state: "STUDENT_STATE",
    zip: "STUDENT_ZIP",
    accountNumber: "ACCOUNT_NUMBER",
  },
  "3921": {
    tin: "EMPLOYEE_SSN",
    firstName: "EMPLOYEE_NAME",
    lastName: "EMPLOYEE_NAME2",
    businessName: "EMPLOYEE_BUSINESS_NAME1",
    nameControl: "EMPLOYEE_NAME_CONTROL",
    address1: "EMPLOYEE_ADDRESS1",
    city: "EMPLOYEE_CITY",
    state: "EMPLOYEE_STATE",
    zip: "EMPLOYEE_ZIP",
    accountNumber: "ACCOUNT_NUMBER",
  },
  "3922": {
    tin: "EMPLOYEE_SSN",
    firstName: "EMPLOYEE_NAME",
    lastName: "EMPLOYEE_NAME2",
    businessName: "EMPLOYEE_BUSINESS_NAME1",
    nameControl: "EMPLOYEE_NAME_CONTROL",
    address1: "EMPLOYEE_ADDRESS_1",
    city: "EMPLOYEE_CITY",
    state: "EMPLOYEE_STATE",
    zip: "EMPLOYEE_ZIP",
    accountNumber: "EMPLOYEE_ACCOUNT_NO",
  },
};

/** The issuer columns to write from the A record, per form. */
function issuerTargets(form: string | null): {
  tin: string;
  name: string;
  name2: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  /** Written only for a foreign issuer, as the IRS country code. */
  country: string;
} {
  if (form === "1099-LTC") {
    return {
      tin: "PAYERS_TIN",
      name: "PAYER_NAME",
      name2: "PAYER_NAME2",
      address1: "PAYER_STREET_ADDRESS_1",
      city: "PAYER_CITY",
      state: "PAYER_STATE",
      zip: "PAYER_ZIP_CODE",
      phone: "PAYER_PHONE",
      country: "PAYER_COUNTRY",
    };
  }
  if (form === "3922") {
    return {
      tin: "CORPORATION_EIN",
      name: "CORPORATION_NAME_1",
      name2: "CORPORATION_NAME_2",
      address1: "CORPORATION_ADDRESS",
      city: "CORPORATION_CITY",
      state: "CORPORATION_STATE",
      zip: "CORPORATION_ZIP",
      phone: "CORPORATION_PHONE",
      country: "CORPORATION_COUNTRY",
    };
  }
  return {
    tin: "FILER_FEDERAL_ID_NUMBER",
    name: "FILER_NAME",
    name2: "FILER_NAME2",
    address1: "FILER_ADDRESS1",
    city: "FILER_CITY",
    state: "FILER_STATE",
    zip: "FILER_ZIP",
    phone: "FILER_PHONE",
    country: "FILER_COUNTRY",
  };
}

/**
 * Pub 1220 writes the payee's name surname first on the First Payee Name Line, separated from
 * the given names by a space or a comma ("DOE JANE" or "DOE, JANE"). Split on the FIRST
 * separator: everything before it is the surname, everything after is the given name or names.
 * A single token is a surname on its own.
 */
export function splitPayeeName(line: string): { first: string; last: string } {
  const value = String(line || "").trim().replace(/\s+/g, " ");
  if (!value) return { first: "", last: "" };
  const m = value.match(/^([^\s,]+)\s*,\s*(.+)$/) || value.match(/^(\S+)\s+(.+)$/);
  if (!m) return { first: "", last: value };
  return { last: m[1], first: m[2] };
}

// ---------------------------------------------------------------------------
// The parser
// ---------------------------------------------------------------------------

export function parsePub1220(text: string, options: Pub1220ParseOptions = {}): Pub1220ParseResult {
  const errors: { line: number; message: string }[] = [];
  const warnings: { line: number; message: string }[] = [];
  const issuers: Pub1220Issuer[] = [];
  const records: Record<string, unknown>[] = [];
  const formTypesSeen: string[] = [];
  const foreignAddresses: Pub1220ForeignAddress[] = [];
  const payeeNames: Pub1220PayeeName[] = [];

  let taxYear: number | null = null;
  let currentForm: FormType | null = null;
  let currentIssuer: Pub1220Issuer | null = null;
  let currentIssuerFields: Record<string, string> = {};
  let currentAmountCodes = "";
  let payeesSinceA = 0;
  // Each payment-amount slot summed in cents over the current issuer's payees, for the C
  // record's control totals. BigInt: a control total runs to 18 digits.
  let slotTotals: bigint[] = new Array(19).fill(0n);

  const { lines: rawLines, joined } = splitPub1220Lines(text);
  if (joined > 0) {
    warnings.push({
      line: 0,
      message:
        "Some records are not separated by line breaks; they were read 750 characters at a time, " +
        "so line numbers below count records rather than lines.",
    });
  }

  rawLines.forEach((rawLine, i) => {
    const lineNo = i + 1;
    const line = rawLine;
    if (line.trim() === "") return; // blank padding between records is harmless

    // Records are 750 characters. Accept a couple either way: some writers keep the two
    // trailing CR/LF positions as data, some strip them.
    if (line.length < PUB1220_RECORD_LENGTH - 2 || line.length > PUB1220_RECORD_LENGTH + 2) {
      errors.push({
        line: lineNo,
        message: `Record is ${line.length} characters; Pub 1220 records are ${PUB1220_RECORD_LENGTH}.`,
      });
      return;
    }
    const padded = line.padEnd(PUB1220_RECORD_LENGTH, " ");
    const type = padded.charAt(0).toUpperCase();

    if (!KNOWN_RECORD_TYPES.has(type)) {
      errors.push({
        line: lineNo,
        message: `Unknown record type "${padded.charAt(0)}". Expected one of T, A, B, C, K, F.`,
      });
      return;
    }

    if (type === "T") {
      const year = read(padded, T_OFFSETS, "PaymentYear");
      if (/^\d{4}$/.test(year)) taxYear = Number(year);
      else warnings.push({ line: lineNo, message: `Transmitter record has no usable payment year ("${year}").` });
      return;
    }

    if (type === "A") {
      // A new issuer closes the previous one.
      currentAmountCodes = read(padded, A_OFFSETS, "AmountCodes");
      const rawTypeOfReturn = read(padded, A_OFFSETS, "TypeOfReturn").toUpperCase();
      currentForm = TYPE_OF_RETURN_TO_FORM[rawTypeOfReturn] ?? null;
      if (!currentForm) {
        warnings.push({
          line: lineNo,
          message: `Type of Return "${rawTypeOfReturn}" is not a form this service files; its payees are still parsed, without form-specific fields.`,
        });
      }
      const label = currentForm ?? rawTypeOfReturn;
      if (label && !formTypesSeen.includes(label)) formTypesSeen.push(label);

      const aYear = read(padded, A_OFFSETS, "PaymentYear");
      if (taxYear === null && /^\d{4}$/.test(aYear)) taxYear = Number(aYear);

      const t = issuerTargets(currentForm);
      currentIssuerFields = {
        [t.tin]: read(padded, A_OFFSETS, "IssuerTIN").replace(/\D/g, ""),
        [t.name]: read(padded, A_OFFSETS, "FirstIssuerNameLine"),
        [t.name2]: read(padded, A_OFFSETS, "SecondIssuerNameLine"),
        [t.address1]: read(padded, A_OFFSETS, "IssuerShippingAddress"),
        [t.city]: read(padded, A_OFFSETS, "IssuerCity"),
        [t.state]: read(padded, A_OFFSETS, "IssuerState"),
        [t.zip]: read(padded, A_OFFSETS, "IssuerZIPCode"),
        [t.phone]: read(padded, A_OFFSETS, "IssuerTelephone"),
      };
      // A foreign issuer (position 52) may write city, province, postal code and country as one
      // 51-position block over the city, state and ZIP (Pub 1220, A record 134-173).
      if (read(padded, A_OFFSETS, "ForeignEntityIndicator") === "1") {
        const reading = readForeignCityBlock(block51(padded, A_OFFSETS, "IssuerCity"), {
          addressLine: read(padded, A_OFFSETS, "IssuerShippingAddress"),
        });
        currentIssuerFields[t.city] = reading.city;
        currentIssuerFields[t.state] = reading.province;
        currentIssuerFields[t.zip] = reading.postalCode;
        if (reading.countryCode) currentIssuerFields[t.country] = reading.countryCode;
        foreignAddresses.push({
          line: lineNo,
          role: "issuer",
          ref: currentIssuerFields[t.tin],
          formType: currentForm,
          reading,
        });
      }

      currentIssuer = {
        tin: read(padded, A_OFFSETS, "IssuerTIN").replace(/\D/g, ""),
        name: read(padded, A_OFFSETS, "FirstIssuerNameLine"),
        formType: currentForm,
        rawTypeOfReturn,
        recordCount: 0,
      };
      issuers.push(currentIssuer);
      payeesSinceA = 0;
      slotTotals = new Array(19).fill(0n);
      return;
    }

    if (type === "B") {
      if (!currentIssuer) {
        errors.push({ line: lineNo, message: "Payee record appears before any issuer (A) record." });
        return;
      }
      records.push(
        buildRecord(
          padded,
          lineNo,
          currentForm,
          currentAmountCodes,
          currentIssuerFields,
          taxYear,
          foreignAddresses,
          { recordIndex: records.length, payeeNames, classificationFor: options.classificationFor }
        )
      );
      payeesSinceA++;
      currentIssuer.recordCount++;
      const bOffs = bOffsets(currentForm);
      for (let slot = 1; slot <= 18; slot++) {
        slotTotals[slot] += signedCents(read(padded, bOffs, `PaymentAmount${slot}`));
      }
      return;
    }

    if (type === "C") {
      const declared = Number(read(padded, C_OFFSETS, "NumberOfPayees") || "0");
      if (declared !== payeesSinceA) {
        errors.push({
          line: lineNo,
          message: `End-of-issuer record says ${declared} payee(s) but ${payeesSinceA} were read.`,
        });
      }
      // Control Total N must equal Payment Amount N summed over the issuer's payees (Pub 1220,
      // C record). FIRE refuses a file whose totals disagree, and a mismatch usually means a
      // payee record was lost or an amount was keyed in the wrong slot.
      for (let slot = 1; slot <= 18; slot++) {
        const stated = signedCents(read(padded, C_OFFSETS, `ControlTotal${slot}`));
        if (stated !== slotTotals[slot]) {
          const code = amountCodeForSlot(slot);
          errors.push({
            line: lineNo,
            message:
              `End-of-issuer control total for amount code ${code} is ${centsText(stated)}, ` +
              `but the payee records add up to ${centsText(slotTotals[slot])}.`,
          });
        }
      }
      return;
    }

    if (type === "K") {
      // State totals. The IRS reconciles these itself and the engine files federally, so
      // they carry nothing the canonical records need.
      return;
    }

    if (type === "F") {
      const declared = Number(read(padded, F_OFFSETS, "TotalNumberOfPayees") || "0");
      if (declared && declared !== records.length) {
        errors.push({
          line: lineNo,
          message: `End-of-transmission record says ${declared} payee(s) but ${records.length} were read.`,
        });
      }
      return;
    }
  });

  if (!issuers.length) {
    errors.push({ line: 0, message: "No issuer (A) record found; this is not a Pub 1220 file." });
  }

  const canonicalForms = formTypesSeen.filter((f) => f in PAYEE_TARGETS_BY_FORM);
  return {
    formType: canonicalForms.length === 1 ? (canonicalForms[0] as FormType) : null,
    taxYear,
    formTypes: formTypesSeen,
    issuers,
    records,
    errors,
    warnings,
    foreignAddresses,
    payeeNames,
  };
}

/** The 51 characters of a city (40) + state (2) + ZIP (9) run, starting at `cityKey`. */
function block51(line: string, offsets: Map<string, { at: number; len: number }>, cityKey: string): string {
  const spec = offsets.get(cityKey);
  return spec ? line.substring(spec.at, spec.at + 51) : "";
}

/** A block that reads as an ordinary US city, state and ZIP: left as the fixed fields say. */
function isUsShaped(block: string): boolean {
  const state = block.slice(40, 42).trim().toUpperCase();
  const zip = block.slice(42, 51).trim();
  return US_STATE_CODES.has(state) && /^\d{5}(?:\d{4})?$/.test(zip);
}

function buildRecord(
  line: string,
  lineNo: number,
  form: FormType | null,
  amountCodes: string,
  issuerFields: Record<string, string>,
  taxYear: number | null,
  foreignAddresses: Pub1220ForeignAddress[],
  names: {
    recordIndex: number;
    payeeNames: Pub1220PayeeName[];
    classificationFor?: Pub1220ParseOptions["classificationFor"];
  }
): Record<string, unknown> {
  const offsets = bOffsets(form);
  const targets = (form && PAYEE_TARGETS_BY_FORM[form]) || RECIPIENT_TARGETS;

  const account = read(line, offsets, "IssuersAccountNumberForPayee");
  const typeOfTin = read(line, offsets, "TypeOfTIN");
  const firstNameLine = read(line, offsets, "FirstPayeeNameLine");
  const secondNameLine = read(line, offsets, "SecondPayeeNameLine");
  const payeeYear = read(line, offsets, "PaymentYear");

  const record: Record<string, unknown> = {
    recordRef: account || `line-${lineNo}`,
    FORM_TYPE: form,
    ...issuerFields,
  };

  // Person or business. TypeOfTIN 1 is an EIN and 2 an SSN/ITIN/ATIN, and either settles it.
  // Blank (the TIN type is unknown) leaves it to the portal's name rules: legal suffix, &, DBA,
  // organisation words, person-name shape.
  const nameControl = read(line, offsets, "NameControl");
  // A 1098-T is always about a student, a person: the IRS form has no business recipient, and a
  // name such as "STUDENT HOUSING CORP" there is a data fault to see, not a company to file.
  // An answer from Resolve Names replaces that reading.
  const answer = names.classificationFor?.(firstNameLine);
  const isBusiness =
    form !== "1098-T" &&
    (answer
      ? answer === "business"
      : typeOfTin === "1" ||
        (typeOfTin !== "2" && classifyName(firstNameLine).classification === "business"));
  names.payeeNames.push({
    recordIndex: names.recordIndex,
    line: lineNo,
    nameLine: firstNameLine,
    secondNameLine,
    nameControl,
    typeOfTin,
  });

  if (isBusiness) {
    // The first name line is the business's name, not a surname to split.
    record[targets.businessName] = firstNameLine;
    if (secondNameLine) record.RECIPIENT_BUSINESS_NAME2 = secondNameLine;
  } else {
    // Surname first, per Pub 1220; the payee name control (the first four letters of the
    // surname) says how many words the surname takes - "VAN DYKE JOHN" with control VAND.
    const parsed = splitPersonNameWithControl(firstNameLine, nameControl);
    record[targets.firstName] = parsed.first || firstNameLine;
    record[targets.lastName] = parsed.last;
    // Middle name and suffix exist only where the form has the split person fields.
    if (targets.firstName === RECIPIENT_TARGETS.firstName) {
      if (parsed.middle) record.RECIPIENT_MIDDLE_NAME = parsed.middle;
      if (parsed.suffix) record.RECIPIENT_SUFFIX = parsed.suffix;
    } else if (parsed.middle) {
      record[targets.firstName] = `${parsed.first} ${parsed.middle}`.trim();
    }
    if (secondNameLine) record[`${targets.lastName}_CONTINUATION`] = secondNameLine;
  }

  // 1099-K, 1098-E, 3921 and 3922 keep the payee in their own columns (PAYEE_NAME,
  // BORROWER_NAME, ...), which say nothing about person or business, so their generators
  // filed every payee as a person: "MAIN STREET BAKERY LLC" became a first and a last name
  // the IRS schema refused. Those generators file through buildSplitRecipientDetail when the
  // record carries the split identity (RECIPIENT_ID_TYPE and RECIPIENT_* names), so the
  // identity read here is written there too, beside the form's own columns.
  if (targets.firstName !== RECIPIENT_TARGETS.firstName) {
    record.RECIPIENT_ID_TYPE = isBusiness ? "1" : "2";
    record.RECIPIENT_NAME_CONTROL = nameControl;
    if (isBusiness) {
      record.RECIPIENT_BUSINESS_NAME1 = firstNameLine;
      if (secondNameLine) record.RECIPIENT_BUSINESS_NAME2 = secondNameLine;
    } else {
      const parsed = splitPersonNameWithControl(firstNameLine, nameControl);
      record.RECIPIENT_FIRST_NAME = parsed.first || firstNameLine;
      record.RECIPIENT_LAST_NAME = parsed.last;
      if (parsed.middle) record.RECIPIENT_MIDDLE_NAME = parsed.middle;
      if (parsed.suffix) record.RECIPIENT_SUFFIX = parsed.suffix;
    }
  }

  record[targets.tin] = read(line, offsets, "PayeesTIN").replace(/\D/g, "");
  record[targets.nameControl] = nameControl;
  record[targets.address1] = read(line, offsets, "PayeeMailingAddress");
  record[targets.city] = read(line, offsets, "PayeeCity");
  record[targets.state] = read(line, offsets, "PayeeState");
  record[targets.zip] = read(line, offsets, "PayeeZIPCode");
  record[targets.accountNumber] = account;

  const corrected = read(line, offsets, "CorrectedReturnIndicator");
  if (corrected) record.CORRECTED_RETURN_INDICATOR = corrected;

  // A "1" here means the payee's address is outside the US, and the city, state and ZIP
  // positions (448-498) may then be one free-format block: "city, province or state, postal
  // code, and the name of the country" (Pub 1220, B record position 287). Read as US fields,
  // that block loses the country and splits the postal code. The reading's confidence goes to
  // the caller in `foreignAddresses`; a reading with no country leaves FOREIGN_COUNTRY empty,
  // which checkRecords refuses, rather than filing a guess.
  const foreign = read(line, offsets, "ForeignCountryIndicator");
  if (foreign && foreign !== "0") {
    record.FOREIGN_COUNTRY_INDICATOR = foreign;
    const reading = readForeignCityBlock(block51(line, offsets, "PayeeCity"), {
      addressLine: read(line, offsets, "PayeeMailingAddress"),
    });
    record[targets.city] = reading.city;
    record[targets.state] = reading.province;
    record[targets.zip] = reading.postalCode;
    if (reading.countryCode) record.FOREIGN_COUNTRY = reading.countryCode;
    if (reading.provinceName && reading.provinceName !== reading.province) {
      record.FOREIGN_PROVINCE_NAME = reading.provinceName;
    }
    foreignAddresses.push({
      line: lineNo,
      role: "payee",
      ref: String(record.recordRef),
      formType: form,
      reading,
    });
  }

  // Amounts: the A record's Amount Codes say which slots carry a value; slot N holds the
  // amount for the Nth code in the Pub 1220 ordinal sequence.
  const declared = new Set(amountCodes.split("").filter((c) => c.trim() !== ""));
  for (let slot = 1; slot <= 18; slot++) {
    const code = amountCodeForSlot(slot);
    if (!code) break;
    if (declared.size && !declared.has(code)) continue;
    const value = centsToDecimal(read(line, offsets, `PaymentAmount${slot}`));
    if (value === null) continue;
    record[amountColumn(form ?? "", code)] = value;
  }

  // The form-specific tail: positions 544 to 750, where a payee record stops being a list of
  // money and starts being the form it is. See pub1220Tail.ts.
  const tail = (form && TAIL_MAPPING_BY_FORM[form]) || null;
  if (tail) {
    for (const [field, spec] of Object.entries(tail.direct)) {
      const value = readTailValue(spec, read(line, offsets, field));
      if (value !== null) record[spec.column] = value;
    }
    for (const [field, translate] of Object.entries(tail.coded ?? {})) {
      for (const [column, value] of Object.entries(translate(read(line, offsets, field)))) {
        record[column] = value;
      }
    }
  }

  // The 1099-LTC insured has its own city, state and ZIP (637-687), which Pub 1220 also lets a
  // foreign record write as one free-format block. The B record's one foreign indicator covers
  // both people, so the insured's block is only re-read when it does not already read as a US
  // address: an insured in Ohio paid through a policyholder in Canada stays in Ohio.
  if (form === "1099-LTC" && foreign && foreign !== "0") {
    const block = block51(line, offsets, "InsuredCity");
    if (block.trim() && !isUsShaped(block)) {
      const reading = readForeignCityBlock(block, { addressLine: read(line, offsets, "InsuredAddress") });
      record.INSURED_CITY = reading.city;
      record.INSURED_STATE = reading.province;
      record.INSURED_ZIP_CODE = reading.postalCode;
      if (reading.countryCode) record.PUB1220_INSURED_COUNTRY = reading.countryCode;
      foreignAddresses.push({
        line: lineNo,
        role: "insured",
        ref: String(record.recordRef),
        formType: form,
        reading,
      });
    }
  }

  // Understood, but this form has nowhere to put it. Carried rather than dropped.
  for (const field of (form && TAIL_CARRIED_ONLY[form]) || []) {
    const value = read(line, offsets, field);
    if (value) record[`PUB1220_${field}`] = value;
  }

  // State figures live in the form-specific tail, where the form has them.
  const stateTax = centsToDecimal(read(line, offsets, "StateIncomeTaxWithheld"));
  if (stateTax !== null && targets.stateTaxWithheld) {
    record[targets.stateTaxWithheld] = stateTax;
  } else if (stateTax !== null) {
    record.PUB1220_STATE_INCOME_TAX_WITHHELD = stateTax;
  }
  const localTax = centsToDecimal(read(line, offsets, "LocalIncomeTaxWithheld"));
  if (localTax !== null) record.PUB1220_LOCAL_INCOME_TAX_WITHHELD = localTax;

  // The year the FILE was written for: the payee record's own, else the transmitter's. What
  // is actually filed is decided by the caller, which re-stamps with stampTaxYear.
  const year = /^\d{4}$/.test(payeeYear) ? Number(payeeYear) : taxYear;
  if (year !== null) stampTaxYear(record, year, form);

  return record;
}

/**
 * Write the tax year onto a record, in whichever column its form reads.
 *
 * The year on a return is the year being filed - the one selected in the app, or named on the
 * API request - not the year that happens to be written inside the file. A customer who
 * exports last year's Pub 1220 file and files it for this year is filing this year. Calling
 * this replaces any year already on the record, so the record, the manifest and the
 * customer's own selection cannot drift apart.
 */
export function stampTaxYear(
  record: Record<string, unknown>,
  taxYear: number,
  formType?: string | null
): void {
  record.TAX_YEAR = taxYear;
  const form = String(formType ?? record.FORM_TYPE ?? "");
  if (form.startsWith("1098")) record["1098_YEAR"] = taxYear;
  else record["1099_YEAR"] = taxYear;
}
